import { camelCaseToSnakeCaseString } from '@/utils/stringCase'

const projects = {
  sectionList: orgId => `/orgs/${orgId}/sections/`,
  sectionDetail: (orgId, sectionId) => `/orgs/${orgId}/sections/${sectionId}/`,
  projectList: orgId => `/orgs/${orgId}/projects/`,
  projectDetail: (orgId, projectId) => `/orgs/${orgId}/projects/${projectId}/`
}
/**
 * This contains BE API end points
 */
export default {
  login: '/login/',
  logout: '/logout/',
  forgotPassword: '/forgot-password/',
  resetPassword: '/reset-password/',
  invitationSignUp: '/invitation-sign-up/',
  user: '/user/',
  userPhoto: '/user/upload-photo/',
  announcements: '/announcements/',
  search: orgId => `/orgs/${orgId}/search/`,
  requestDemo: orgId => `/orgs/${orgId}/request-demo/`,
  userDetails: userId => `/users/${userId}/`,
  changeRank: orgId => `/orgs/${orgId}/change_rank/`,
  sendTeamInvite: ({ orgId, teamId }) => `/orgs/${orgId}/teams/${teamId}/send-invite/`,
  sendOrgInvite: orgId => `/orgs/${orgId}/send-invite/`,
  checkInHistory: ({ orgId, goalId }) => `/orgs/${orgId}/goals/${goalId}/checkin_history/`,
  goalContributions: ({ orgId, goalId }) => `/orgs/${orgId}/goals/${goalId}/set_contributions/`,
  CFDefinitions: orgId => `/orgs/${orgId}/custom_field_definitions/`,
  CFDefinitionsDetail: ({ orgId, customFieldId }) =>
    `/orgs/${orgId}/custom_field_definitions/${customFieldId}/`,
  GoalKRDetail: ({ orgId, goalId, krId }) => `/orgs/${orgId}/goals/${goalId}/key_results/${krId}/`,
  keyResults: orgId => `/orgs/${orgId}/key_results/`,
  KRDetail: ({ orgId, krId }) => `/orgs/${orgId}/key_results/${krId}/`,
  krUserProps: ({ orgId, entityId }) => `/orgs/${orgId}/key_results/${entityId}/user_props/`,
  teams: orgId => `/orgs/${orgId}/teams/`,
  team: ({ orgId, teamId }) => `/orgs/${orgId}/teams/${teamId}/`,
  teamDescendants: ({ orgId, teamId }) => `/orgs/${orgId}/teams/${teamId}/descendants/`,
  fileUpload: (orgId, type = 'Images') => `/api/orgs/${orgId}/ckeditor-upload/?type=${type}`,
  teamVision: ({ orgId, teamId }) => `/orgs/${orgId}/teams/${teamId}/vision/`,
  teamMembershipDetails: ({ orgId, teamId, userId }) =>
    `/orgs/${orgId}/teams/${teamId}/memberships/${userId}/`,
  teamMemberships: ({ orgId, teamId }) => `/orgs/${orgId}/teams/${teamId}/memberships/`,
  cloneGoal: ({ orgId, goalId }) => `/orgs/${orgId}/goals/${goalId}/clone/`,
  activity: orgId => `/orgs/${orgId}/activity/`,
  initiativesBoard: orgId => `orgs/${orgId}/initiatives-board/`,
  notifications: orgId => `/orgs/${orgId}/notifications/`,
  notification: ({ orgId, id }) => `/orgs/${orgId}/notifications/${id}/`,
  notificationsSummary: orgId => `orgs/${orgId}/unread-notification-summary/`,
  readAllNotifications: orgId => `/orgs/${orgId}/mark-notifications-read/`,
  goals: orgId => `/orgs/${orgId}/goals/`,
  goalDetail: ({ orgId, goalId }) => `/orgs/${orgId}/goals/${goalId}/`,
  generateGoals: orgId => (orgId ? `/orgs/${orgId}/ai/generate-goals/` : `/ai/generate-goals/`),
  generateInitiatives: (orgId, krId) =>
    `/orgs/${orgId}/ai/key_results/${krId}/generate-initiatives/`,
  goalUserProps: ({ orgId, entityId }) => `/orgs/${orgId}/goals/${entityId}/user_props/`,
  goalEvents: ({ orgId, goalId }) => `/orgs/${orgId}/goals/${goalId}/events/`,
  initiativeDetail: ({ orgId, initiativeId }) => `/orgs/${orgId}/initiatives/${initiativeId}/`,
  initiativeBulkAction: orgId => `/orgs/${orgId}/initiatives/bulk-action/`,
  initiativeUserProps: ({ orgId, entityId }) =>
    `/orgs/${orgId}/initiatives/${entityId}/user_props/`,
  oauthClients: '/oauth/clients/',
  oauthClientDetail: id => `/oauth/clients/${id}/`,
  ideaList: orgId => `/orgs/${orgId}/idea-lists/`,
  ideaListDetails: (orgId, ideaListId) => `/orgs/${orgId}/idea-lists/${ideaListId}/`,
  ideasList: (orgId, listId) => `/orgs/${orgId}/idea-lists/${listId}/ideas/`,
  ideaDetails: (orgId, ideaId) => `/orgs/${orgId}/ideas/${ideaId}/`,
  allIdeas: orgId => `/orgs/${orgId}/ideas/`,
  ideaEvents: (orgId, ideaId) => `/orgs/${orgId}/ideas/${ideaId}/events/`,
  ideaEventDetails: (orgId, ideaId, eventId) => `/orgs/${orgId}/ideas/${ideaId}/events/${eventId}/`,
  orgDetails: orgId => `/orgs/${orgId}/`,
  timezones: `/timezones/`,
  timezoneDetails: name => `/timezones/${name}/`,
  notificationSettings: orgId => `/orgs/${orgId}/notification-settings/`,
  goalEventDetails: ({ orgId, goalId, eventId }) =>
    `/orgs/${orgId}/goals/${goalId}/events/${eventId}/`,
  initiativeEvents: ({ orgId, initiativeId }) =>
    `/orgs/${orgId}/initiatives/${initiativeId}/events/`,
  initiativeEventDetails: ({ orgId, initiativeId, eventId }) =>
    `/orgs/${orgId}/initiatives/${initiativeId}/events/${eventId}/`,
  initiativeCustomFields: ({ orgId, initiativeId }) =>
    `/orgs/${orgId}/initiatives/${initiativeId}/custom_fields/`,
  enabledCustomFields: orgId => `/orgs/${orgId}/enabled_initiative_cfs/`,
  enabledCustomFieldDetails: ({ orgId, enabledFieldId }) =>
    `/orgs/${orgId}/enabled_initiative_cfs/${enabledFieldId}/`,
  initiativeCustomFieldDetails: ({ orgId, initiativeId, fieldId }) =>
    `/orgs/${orgId}/initiatives/${initiativeId}/custom_fields/${fieldId}/`,
  displayInitiativeFields: orgId => `/orgs/${orgId}/display_initiative_fields/`,
  displayInitiativeFieldDetails: ({ orgId, displayFieldId }) =>
    `/orgs/${orgId}/display_initiative_fields/${displayFieldId}/`,
  invitations: '/invitations/',
  invitationDetails: key => `/invitations/${key}/`,
  orgInvitations: orgId => `/orgs/${orgId}/invitations/`,
  orgInvitationDetails: ({ orgId, key }) => `/orgs/${orgId}/invitations/${key}/`,
  allEvents: orgId => `/orgs/${orgId}/all_events/`,
  allInitiatives: orgId => `/orgs/${orgId}/initiatives/`,
  initiativeSubscribers({ orgId, initiativeId }) {
    const initiativeDetails = this.initiativeDetail({ orgId, initiativeId })
    return `${initiativeDetails}subscribers/`
  },
  krSubscribers({ orgId, krId }) {
    return `${this.KRDetail({ orgId, krId })}subscribers/`
  },
  goalSubscribers({ orgId, goalId }) {
    return `${this.goalDetail({ orgId, goalId })}subscribers/`
  },
  krEvents: ({ orgId, krId }) => `orgs/${orgId}/key_results/${krId}/events/`,
  krEventDetails: ({ orgId, krId, eventId }) =>
    `orgs/${orgId}/key_results/${krId}/events/${eventId}/`,
  filterConfigurations: '/api/search-filter-config/',
  roleTemplates: '/templates/roles',
  companyTemplates: '/templates/companies',
  functionTemplates: '/templates/functions',
  goalTemplates: '/templates/goals',
  starredList: orgId => `orgs/${orgId}/starred/`,
  starredDetails: (orgId, starredId) => `orgs/${orgId}/starred/${starredId}/`,
  goalRelationships: orgId => `orgs/${orgId}/goal_relationships/`,
  billingProducts: '/billing/products/',
  billingSubscription: orgId => `orgs/${orgId}/billing/subscription/`,
  billingSubscribe: orgId => `orgs/${orgId}/billing/subscribe/`,
  stripeCheckoutSessionDetail: ({ orgId, sessionId }) =>
    `orgs/${orgId}/billing/stripe_checkout_sessions/${sessionId}/`,
  stripeCustomerPortal: orgId => `orgs/${orgId}/billing/stripe_customer_portal/`,
  activeSubscription: orgId => `/orgs/${orgId}/billing/product/`,
  satisfyFeature: (orgId, featureName) =>
    `/orgs/${orgId}/satisfies/${camelCaseToSnakeCaseString(featureName)}/`,
  orgs: 'orgs/',
  requestSignUp: 'sign-up-requests/',
  changePassword: 'change-password/',
  verifySignup: id => `sign-up-requests/${id}/`,
  summarizeInitiative: (orgId, initiativeId) =>
    `orgs/${orgId}/initiatives/${initiativeId}/summarize/`,
  generateNql: orgId => `orgs/${orgId}/ai/generate-nql/`,
  userLists: orgId => `orgs/${orgId}/user-lists/`,
  userListDetail: (orgId, userListId) => `orgs/${orgId}/user-lists/${userListId}`,
  checkPublicDomain: domain => `check-public-domain/${domain}/`,
  approvedDomains: orgId => `orgs/${orgId}/approved-domains/`,
  checkinTemplate: orgId => `orgs/${orgId}/checkin-template/`,
  autoJoinOrgs: `autojoin-orgs/`,
  joinOrg: orgId => `orgs/${orgId}/join/`,
  demoOrgSources: `demo-org-sources/`,
  authTokens: orgId => `orgs/${orgId}/auth_tokens/`,
  authTokenDetail: (orgId, id) => `orgs/${orgId}/auth_tokens/${id}/`,
  ...projects
}
